import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import type { AddRfidGroupModalStageProps } from 'content/modals/AddRfidGroupModal';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import CancelIcon from 'components/Icons/CancelIcon';
import FilledTextField from 'components/MuiCustom/FilledTextField';

import { useAppDispatch, useAppSelector } from 'redux-store';
import type { RfidId } from 'redux-store/slices/ui/types';
import addRfidGroupThunk from 'redux-store/thunks/rfidGroups/addRfidGroupThunk';
import addRfidToRfidGroupThunk from 'redux-store/thunks/rfidGroups/addRfidToRfidGroupThunk';
import getAllRfidGroupsThunk from 'redux-store/thunks/rfidGroups/getAllRfidGroupsThunk';

import RfidGroupsRfidItem from './RfidGroupsRfidItem';

const AddRfidGroupSetRfidsContent: FC<AddRfidGroupModalStageProps> = ({
  state,
  groupID,
  setState,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();

  const selectedRfids = useMemo(() => state.rfids, [state.rfids]);
  const setselectedRfids = useCallback(
    (rfids: RfidId[]) => {
      setState(prev => ({ ...prev, rfids }));
    },
    [setState],
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);

  const rfidsList = useAppSelector(appState => appState.rfids.items);
  const rfidGroups = useAppSelector(appState => appState.rfidGroups.items);

  // Get the array of RFID IDs in the selected group
  const selectedGroupRfids = useMemo(() => {
    return rfidGroups.find(group => group.id === groupID)?.rfids || [];
  }, [rfidGroups, groupID]);

  // Filter RFIDs not in the selected group
  const unassignedRfids = useMemo(() => {
    return rfidsList.filter(rfid => !selectedGroupRfids.includes(rfid.id));
  }, [rfidsList, selectedGroupRfids]);

  const fuse = useMemo(
    () =>
      new Fuse(unassignedRfids, {
        keys: ['rfid', 'name'],
        threshold: 0.4,
      }),
    [unassignedRfids],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.rfid));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  const filteredRfids = useMemo(
    () =>
      searchResults
        ? unassignedRfids.filter(rfid => searchResults.includes(rfid.rfid))
        : unassignedRfids,
    [unassignedRfids, searchResults],
  );

  const filteredSelectedRfids = useMemo(
    () => selectedRfids.filter(rfid => filteredRfids.some(r => r.id === rfid)),
    [filteredRfids, selectedRfids],
  );

  const handleCreateGroup = useCallback(async () => {
    let result = undefined;

    const filteredRfidIds = filteredRfids
      .filter(r => filteredSelectedRfids.includes(r.id))
      .map(rfid => rfid.id);

    try {
      setLoading(true);

      result = await dispatch(
        addRfidGroupThunk({
          rfidGroup: {
            name: state.name,
            rfids: filteredRfidIds,
          },
        }),
      ).unwrap();

      if (!result.success) {
        setError(
          t(
            'common:pages.rfid_groups.add_rfid_group.errors.error_creating_group_with_error',
            { error: result.error },
          ),
        );
        setLoading(false);

        return;
      }

      setError(null);

      setState({ name: '', rfids: [], stage: 'success' });

      await dispatch(getAllRfidGroupsThunk({})).unwrap();

      setTimeout(() => {
        setState({ name: '', rfids: [], stage: 'name' });
        onClose();
      }, 1250);
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          filteredRfidIds: filteredRfidIds,
          state,
          result,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    filteredRfids,
    filteredSelectedRfids,
    onClose,
    setState,
    state,
    t,
  ]);

  const handleAddRfidsToGroup = useCallback(async () => {
    let result = undefined;

    const filteredRfidIds = filteredRfids
      .filter(r => filteredSelectedRfids.includes(r.id))
      .map(rfid => rfid.id);

    try {
      setLoading(true);
      if (!groupID) return;
      result = await dispatch(
        addRfidToRfidGroupThunk({
          rfidGroupId: groupID,
          data: {
            rfids: filteredRfidIds,
          },
        }),
      ).unwrap();

      if (result.success) {
        await dispatch(getAllRfidGroupsThunk({})).unwrap();

        setError(null);

        setState({ name: '', rfids: [], stage: 'success' });

        setTimeout(() => {
          setState({ name: '', rfids: [], stage: 'name' });
          onClose();
        }, 1250);
      } else {
        setError(result.error as string);
      }
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          filteredRfidIds: filteredRfidIds,
          state,
          result,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    filteredRfids,
    filteredSelectedRfids,
    groupID,
    onClose,
    setState,
    state,
  ]);

  return (
    <Box>
      <Box
        sx={{
          height: '55vh',
          overflow: 'auto',
          paddingBottom: 5,
        }}
      >
        <Box mb={4} width="100%">
          <Typography
            variant="h2"
            textAlign="center"
            mb={2}
            data-testid="add-rfid-group-modal-title"
          >
            {t('common:pages.rfid_groups.add_rfid_group.add_rfids_to_name', {
              name: state.name,
            })}
          </Typography>
          <Typography variant="body1" textAlign="center">
            {t(
              'common:pages.rfid_groups.add_rfid_group.select_rfids_description',
            )}
          </Typography>
        </Box>
        <Box mb={4} width="100%">
          <ListItemButton
            sx={{
              backgroundColor: theme.vars.palette.grey[100],
              borderRadius: 2,
            }}
            onClick={() =>
              setState(prev => ({
                ...prev,
                stage: 'addRfid',
                rfids: selectedRfids,
              }))
            }
            data-testid="add-new-rfid-button"
          >
            <ListItemText
              color="primary"
              primaryTypographyProps={{
                style: {
                  color: theme.vars.palette.primary.dark,
                  fontWeight: 500,
                },
              }}
              primary={t('common:pages.rfid_groups.add_new_rfid')}
            />
            <IconButton edge="end" disableRipple>
              <AddCircleOutlineRoundedIcon color="primary" />
            </IconButton>
          </ListItemButton>
        </Box>
        <Box mb={3} width="100%">
          <form
            onSubmit={e => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <FilledTextField
              value={searchString}
              onChange={e => setSearchString(e.target.value)}
              fullWidth
              label={t(
                'common:pages.rfid_groups.add_rfid_group.search_for_name_or_serial',
              )}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={searchResults ? handleClearSearch : handleSearch}
                  >
                    {searchResults ? <CancelIcon /> : <SearchIcon />}
                  </IconButton>
                ),
                inputProps: { 'data-testid': 'search-input' },
              }}
            />
          </form>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          overflow="auto"
          flexGrow={0}
          flexShrink={1}
          flexBasis="auto"
          width="100%"
        >
          {filteredRfids.map(filteredRfid => (
            <RfidGroupsRfidItem
              key={filteredRfid.rfid}
              rfid={filteredRfid}
              selectedRfids={selectedRfids}
              setselectedRfids={setselectedRfids}
            />
          ))}
          {filteredRfids.length === 0 ? (
            <Typography
              variant="body1"
              textAlign="center"
              data-testid="no-rfid-found"
            >
              {t('common:pages.rfid_groups.add_rfid_group.no_rfids_found')}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <ErrorText error={error} mt={2} />
      <Box width="100%" pt={2}>
        <Button
          variant="flat"
          fullWidth
          size="large"
          onClick={groupID ? handleAddRfidsToGroup : handleCreateGroup}
          disabled={
            loading || (!!groupID && filteredSelectedRfids.length === 0)
          }
          data-testid="submit"
        >
          {t('common:add')}
        </Button>
      </Box>
    </Box>
  );
};

export default AddRfidGroupSetRfidsContent;
